import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronUp, ChevronDown } from "lucide-react";

const HIGHLIGHT_CLASS = "mango-find-hit";
const ACTIVE_CLASS = "mango-find-active";
const INPUT_HIT_CLASS = "mango-find-input-hit";
const INPUT_ACTIVE_CLASS = "mango-find-input-active";

// Walk all descendant text nodes of `root` and wrap every match of `needle`
// (case-insensitive) in a <mark> element with class `mango-find-hit`. We
// skip nodes that are themselves inside existing <mark> highlights so the
// function is idempotent across keystrokes.
function highlight(root, needle) {
  if (!root || !needle) return [];
  const matches = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (n.parentElement?.classList?.contains(HIGHLIGHT_CLASS)) return NodeFilter.FILTER_REJECT;
      if (n.parentElement?.closest?.("[data-find-ignore=\"true\"]")) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  let n = walker.nextNode();
  while (n) { nodes.push(n); n = walker.nextNode(); }
  const needleLower = needle.toLowerCase();
  for (const node of nodes) {
    const text = node.nodeValue;
    const low = text.toLowerCase();
    let idx = low.indexOf(needleLower);
    if (idx === -1) continue;
    const frag = document.createDocumentFragment();
    let cursor = 0;
    while (idx !== -1) {
      if (idx > cursor) frag.appendChild(document.createTextNode(text.slice(cursor, idx)));
      const mark = document.createElement("mark");
      mark.className = HIGHLIGHT_CLASS;
      mark.textContent = text.slice(idx, idx + needle.length);
      frag.appendChild(mark);
      matches.push({ el: mark, kind: "text" });
      cursor = idx + needle.length;
      idx = low.indexOf(needleLower, cursor);
    }
    if (cursor < text.length) frag.appendChild(document.createTextNode(text.slice(cursor)));
    node.parentNode.replaceChild(frag, node);
  }
  // Also match form inputs — <input type=text> and <textarea>. We can't
  // inject <mark> into their `value`, so we highlight the whole element
  // with a soft background. The caller can still count/navigate them.
  const inputs = root.querySelectorAll("input[type='text'], input:not([type]), textarea");
  for (const el of inputs) {
    if (el.closest("[data-find-ignore=\"true\"]")) continue;
    const v = (el.value || "").toLowerCase();
    if (v && v.includes(needleLower)) {
      el.classList.add(INPUT_HIT_CLASS);
      matches.push({ el, kind: "input" });
    }
  }
  return matches;
}

// Flatten the DOM back by replacing every mango-find-hit <mark> with its
// text content, then normalizing the parent so adjacent text nodes merge.
function clearHighlights(root) {
  if (!root) return;
  const marks = root.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`);
  marks.forEach((m) => {
    const txt = document.createTextNode(m.textContent || "");
    m.parentNode.replaceChild(txt, m);
  });
  root.querySelectorAll(`.${INPUT_HIT_CLASS}`).forEach((el) => {
    el.classList.remove(INPUT_HIT_CLASS);
    el.classList.remove(INPUT_ACTIVE_CLASS);
  });
  // Collapse adjacent text nodes created by the replacement.
  root.normalize?.();
}

/**
 * Floating "Find in note" bar. Pass a `containerRef` whose current element
 * is the scrollable region of the note editor (title + body + attachments).
 * Supports Enter → next, Shift+Enter → previous, Esc → close.
 */
export default function FindInNote({ containerRef, open, onClose }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const matchesRef = useRef([]);
  const inputRef = useRef(null);

  // Rebuild highlights whenever the needle changes.
  useEffect(() => {
    if (!open) return;
    const root = containerRef?.current;
    if (!root) return;
    clearHighlights(root);
    if (!q.trim()) { matchesRef.current = []; setCursor(0); return; }
    matchesRef.current = highlight(root, q);
    setCursor(matchesRef.current.length > 0 ? 1 : 0);
    // Scroll + activate the first hit.
    if (matchesRef.current[0]) activate(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  // Clean up on close.
  useEffect(() => {
    if (!open) {
      const root = containerRef?.current;
      clearHighlights(root);
      matchesRef.current = [];
      setQ(""); setCursor(0);
    } else {
      // Autofocus with a tiny delay so Radix dialog focus-trap doesn't steal it.
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activate = (idx) => {
    const hits = matchesRef.current;
    if (!hits.length) return;
    // Clear previous active state on both kinds of hits.
    hits.forEach((h) => {
      if (h.kind === "text") h.el.classList.remove(ACTIVE_CLASS);
      else h.el.classList.remove(INPUT_ACTIVE_CLASS);
    });
    const i = ((idx % hits.length) + hits.length) % hits.length;
    const hit = hits[i];
    if (hit.kind === "text") hit.el.classList.add(ACTIVE_CLASS);
    else hit.el.classList.add(INPUT_ACTIVE_CLASS);
    hit.el.scrollIntoView({ behavior: "smooth", block: "center" });
    setCursor(i + 1);
  };

  const next = () => activate(cursor); // cursor is 1-based; next match index == cursor
  const prev = () => activate(cursor - 2);

  const total = matchesRef.current.length;

  if (!open) return null;

  return (
    <div
      data-find-ignore="true"
      className="absolute top-2 right-2 z-30 flex items-center gap-1 rounded-full bg-white/95 dark:bg-stone-900/95 backdrop-blur border border-stone-200 dark:border-stone-700 shadow-md px-2 py-1"
      data-testid="editor-find-bar"
    >
      <Search className="h-4 w-4 text-stone-400 shrink-0" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.shiftKey ? prev() : next(); }
          // Escape is handled by the capture-phase document listener above
          // (needed to pre-empt Radix Dialog's native Escape handler).
        }}
        placeholder="Find in note"
        className="w-44 sm:w-56 bg-transparent outline-none text-sm py-1"
        data-testid="editor-find-input"
      />
      <span className="text-[11px] text-stone-500 tabular-nums w-14 text-center shrink-0" data-testid="editor-find-count">
        {q ? `${total ? cursor : 0}/${total}` : "0/0"}
      </span>
      <button
        onClick={prev}
        disabled={total === 0}
        className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-40"
        aria-label="Previous match"
        data-testid="editor-find-prev"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={next}
        disabled={total === 0}
        className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-40"
        aria-label="Next match"
        data-testid="editor-find-next"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={onClose}
        className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
        aria-label="Close find"
        data-testid="editor-find-close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
